import { useState, useEffect, useContext } from 'react';
import { COSMOS_API, POST_ENDPOINT } from '../api/API';
import { AdminContext } from '../contexts/AdminContext';
import {
  Admin,
  ApplicationRoles,
  rolePermission,
  RoleObject,
  ScopeType,
  adminCanRemoveRole,
  assignableAdminRoles,
  assignableRoleScopeIds,
  assignableRoleScopeTypes
} from '../interfaces/ApplicationInterfaces';
import './AddAdmin.css';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { forwardRef } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { RemoveCircleOutlineOutlined } from '@mui/icons-material';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import APPSETTINGS from '../settings.json';
import { useSnackbar } from 'notistack';
import Stack from '@mui/material/Stack';
import Delete from '@mui/icons-material/Delete';

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Not a precise validation, but also not terribly important for the most part - a user has to sign in with an email to line up with the admin record.
// We do also exclude the illegal cosmosDB characters.
const inEmailFormat = (idVal: string) => {
  let splitDomain = idVal ? idVal.split("@") : [];
  let splitTLD = (splitDomain.length == 2) ? splitDomain[1].split(".") : [];
  return idVal && (splitDomain.length == 2) && (splitDomain[0].length > 0) && (splitTLD.length >= 2) && (splitTLD[0].length > 0) && (splitTLD[splitTLD.length-1].length > 0) &&
  (!(idVal.includes("\\") || idVal.includes("#") || idVal.includes("?") || idVal.includes("/")));
}

const AddAdmin = (props: {
  user?: Admin;
  setUser: React.Dispatch<React.SetStateAction<Admin | undefined>>;
  cancel: () => void;
  searchAdmins: () => void;
  modifyAdmin: boolean;
  organizations: Array<{ id: string; name: string }>;
}) => {
  const [id, setId] = useState<string>('');
  const [managedApplications, setManagedApplications] = useState<Array<string>>(
    []
  );
  const [applications, setApplications] = useState<Array<string>>([]);
  const [selectedApps, setSelectedApps] = useState<Array<string>>([]);
  const [newAdminApps, setNewAdminApps] = useState<ApplicationRoles>({});
  const [appToModify, setAppToModify] = useState<string>('');
  const [modifyAppRole, setModifyAppRole] = useState<string>('');
  const [superAdmin, setSuperAdmin] = useState(false);
  const [organizationAdmin, setOrganizationAdmin] = useState(false);
  const [savingAdmin, setSavingAdmin] = useState<boolean>(false);
  const [globalNoEdit, setGlobalNoEdit] = useState(false);
  const [roleList, setRoleList] = useState<Array<string>>([]);

  const [groupToModify, setGroupToModify] = useState<string>('');
  //const [modifyGroupRole, setModifyGroupRole] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedRoleScope, setSelectedRoleScope] = useState<string>('');
  const [roleManagement, setRoleManagement] = useState<Record<string, Record<string, Set<string>>>>({});
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { adminCredentials, getToken } = useContext(AdminContext);

  const [assignedOrganization, setAssignedOrganization] = useState<string>(
    props.user && !props.user?.isSuperAdmin
      ? props.user.organizationId
      : props.organizations.length > 0
      ? props.organizations[0].id
      : adminCredentials.organizationId
  );

  const [additionalOrganizations, setAdditionalOrganizations] = useState<string[]>([]);
  const [nextAddOrganization, setNextAddOrganization] = useState<string>('');

  useEffect(() => {
    COSMOS_API.POST(
      { permission: 'ManageAdmins' },
      POST_ENDPOINT.GetApplicationsWithPermission,
      getToken()
    ).then((resp) => {
      const apps: Array<string> = resp.respBody.apps.sort(
        (a: string, b: string) =>
          adminCredentials.allApplications[a]?.name <=
            adminCredentials.allApplications[b]?.name
            ? -1
            : 1
      );
      setManagedApplications(apps);
      setApplications(apps);
      
      if(apps.length === 1){
        setAppToModify(apps[0])
        setNewAdminApps({[apps[0]]: modifyAppRole})
      }

      // COSMOS_API.POST({}, POST_ENDPOINT.GetRoles, getToken()).then((resp) => {
      //   setRoleList(resp.respBody.roles);
      // });

      if (props.user !== undefined) {
        if (
          props.user.id === adminCredentials.id ||
          (!adminCredentials.isSuperAdmin && props.user.isSuperAdmin) ||
          (!adminCredentials.isSuperAdmin &&
            !adminCredentials.isOrganizationAdmin &&
            props.user.isOrganizationAdmin)
        ) {
          setGlobalNoEdit(true);
        }
        setId(props.user.id);
        setSuperAdmin(props.user.isSuperAdmin);
        if (props.user.applicationSpecificRoles !== null) {
          const currentApps = Object.keys(props.user.applicationSpecificRoles);
          setSelectedApps([...currentApps]);
          var appsUserCanModify: ApplicationRoles = {};
          currentApps.forEach((permissionId: string) => {
            if (apps.includes(permissionId)) {
              appsUserCanModify[permissionId] =
                props.user!.applicationSpecificRoles[permissionId];
            }
          });
          setNewAdminApps({ ...appsUserCanModify });
        }
        if (props.user.roles && props.user.roles.length > 0) {
          let _roleManagement: Record<string, Record<string, Set<string>>> = {};
          props.user.roles.forEach((role: RoleObject) => {
            if (_roleManagement.hasOwnProperty(role['roleId'])) {
              if (!_roleManagement[role['roleId']].hasOwnProperty(role['scope']['type'])) {
                _roleManagement[role['roleId']][role['scope']['type']] = new Set();
              }
              _roleManagement[role['roleId']][role['scope']['type']].add(role['scope']['objectId']);
            } else {
              _roleManagement[role['roleId']] = { [role['scope']['type']]: new Set() };
              _roleManagement[role['roleId']][role['scope']['type']].add(role['scope']['objectId']);
            }
          })
          setRoleManagement(_roleManagement);
        }
      }
    });
  }, []);

  useEffect(() => {
    setSelectedApps([]);
    setNewAdminApps({});
    setAppToModify('');
    setGlobalNoEdit(false);
    if (props.user !== undefined) {
      const apps: Array<string> = adminCredentials.adminApplications
        .filter((value) =>
          rolePermission(
            'manageApplicationAdmins',
            value.role,
            adminCredentials.roleSpecificPermissions,
            adminCredentials.isSuperAdmin
          )
        )
        .map((key: any) => {
          return key.id;
        });
      if (
        props.user.id === adminCredentials.id ||
        (!adminCredentials.isSuperAdmin && props.user.isSuperAdmin)
      ) {
        setGlobalNoEdit(true);
      }
      setId(props.user.id);
      setSuperAdmin(props.user.isSuperAdmin);
      setOrganizationAdmin(props.user.isOrganizationAdmin);
      setAssignedOrganization(props.user.organizationId);
      setAdditionalOrganizations(props.user.administeredOrganizations ? props.user.administeredOrganizations.filter(aorg => aorg !== props.user?.organizationId) : []);
      setNextAddOrganization('');
      if (props.user.applicationSpecificRoles !== null) {
        const currentApps = Object.keys(props.user.applicationSpecificRoles);
        setSelectedApps([...currentApps]);
        var appsUserCanModify: ApplicationRoles = {};
        Object.keys(props.user.applicationSpecificRoles).forEach(
          (permissionId: string) => {
            if (apps.includes(permissionId)) {
              appsUserCanModify[permissionId] =
                props.user!.applicationSpecificRoles[permissionId];
            }
          }
        );
        setNewAdminApps({ ...appsUserCanModify });
      }
    }
  }, [props.user]);

  useEffect(() => {
    if(managedApplications.length === 1){
      if(!superAdmin && ! organizationAdmin){
        setAppToModify(managedApplications[0])
        setNewAdminApps({[managedApplications[0]]: modifyAppRole})
      }
      if (!organizationAdmin) {
        setAdditionalOrganizations([]);
      }
    }
  }, [superAdmin, organizationAdmin])

  // useEffect(() => {
  //   if (superAdmin) {
  //     const allApps = Object.keys(adminCredentials.allApplications);
  //     setSelectedApps(allApps);

  //     var superAdminApps: ApplicationRoles = {};

  //     for (var appId of allApps) {
  //       superAdminApps[appId] = "Supervisor";
  //     }

  //     setNewAdminApps({ ...superAdminApps });
  //     setAppToModify(allApps[0]);
  //     setModifyAppRole("Supervisor");
  //   }
  // }, [superAdmin]);

  const handleChange = (e: any, values: Array<string>) => {
    if (!adminCredentials.isSuperAdmin && values.length < selectedApps.length) {
      // Check if Admin can remove this specific app (adding is already restricted)
      var editedItem = selectedApps.filter(
        (v: string) => !values.find((val) => val === v)
      );
      var isAllowed = editedItem.every((option: string) =>
        managedApplications.includes(option)
      );
      if (!isAllowed) return;
    }

    setSelectedApps(applications.filter((app) => values.includes(app)));
    if (appToModify === undefined && values.length > 0) {
      setAppRoleToModify('', values[0]);
    } else if (values.length === 0) {
      setAppRoleToModify('', null);
    }

    var refreshAdminApps: ApplicationRoles = {};

    for (var appObj of values) {
      var appId = appObj;
      if (newAdminApps.hasOwnProperty(appId)) {
        refreshAdminApps[appId] = newAdminApps[appId];
      } else {
        refreshAdminApps[appId] = '';
      }
    }

    setNewAdminApps({ ...refreshAdminApps });
  };

  const setAppRoleToModify = (e: any, value: string | null) => {
    setAppToModify(value ? value : '');
    setModifyAppRole(value ? newAdminApps[value] : 'Supervisor');
  };

  const restSaveAdmin = (e: any) => {
    if (globalNoEdit) return;
    setSavingAdmin(true);

    if(!organizationAdmin && !superAdmin && JSON.stringify(newAdminApps) === '{}'){
      enqueueSnackbar(
        `Admin ${id.toLocaleLowerCase()} was not updated. Please assign application permissions or make the admin a super admin or organziation admin.`,
        { variant: 'error' }
      );
      setSavingAdmin(false);
      return;
    }
    let roles: any[] = [];
    if (Object.keys(roleManagement).length > 0) {
      Object.keys(roleManagement).forEach((roleName: string) => {
        Object.keys(roleManagement[roleName]).forEach((scope: string) => {
          Array.from(roleManagement[roleName][scope]).forEach((groupId: string) => {
            let roleObj = {
              "scope": {
                "type": scope,
                "objectId": groupId == "" ? null : groupId
              },
              "roleId": roleName
            }
            roles.push(roleObj);
          });
        })
      });
    }
    const body = {
      id: id.toLocaleLowerCase(),
      securityGroups: [],
      applicationAdminIds: Object.keys(newAdminApps),
      applicationSpecificRoles: { ...newAdminApps },
      roles: roles,
      isSuperAdmin: superAdmin,
      isOrganizationAdmin: organizationAdmin,
      isActive: true,
      organizationId: assignedOrganization,
      administeredOrganizations: ((adminCredentials.isSuperAdmin || adminCredentials.multiOrg) && (!superAdmin) && organizationAdmin) ? [...additionalOrganizations, assignedOrganization] : undefined
    };
    COSMOS_API.POST(body, POST_ENDPOINT.SaveEmployeeData, getToken()).then(
      (res) => {
        if (res.ok) {
          if (props.user === undefined) {
            props.setUser({ ...body });
            enqueueSnackbar(`Admin sucessfully created.`, {
              variant: 'success',
            });
          } else
            enqueueSnackbar(`Admin sucessfully updated.`, {
              variant: 'success',
            });
          props.searchAdmins();
          props.cancel();
        } else {
          if (props.user === undefined)
            enqueueSnackbar(
              `Admin ${body.id} was not created. Error: ${res.respBody.errorMessage ?? ''
              }`,
              { variant: 'error' }
            );
          else
            enqueueSnackbar(
              `Admin ${body.id} was not updated. Error: ${res.respBody.errorMessage ?? ''
              }`,
              { variant: 'error' }
            );
          setSavingAdmin(false);
        }
      }
    );
  };
  const handleRoleChange = (e: any, value: string | null) => {
    if (!value) return;
    setSelectedRole(value);
  }
  const handleRoleScopeChange = (e: any, value: string | null) => {
    if (!value) return;
    if (value === "Global") {
      setGroupToModify("");
    }
    setSelectedRoleScope(value);
  }

  const handleGroupChange = (e: any, value: string | null) => {
    if (!value) return;
    setGroupToModify(value);
  }

  const deleteRoleFromUser = (role: string, scope: string, id: string) => {
    let _newRoles: Record<string, Record<string, Set<string>>> = {};
    if (roleManagement) {
      _newRoles = Object.assign({}, roleManagement);
      _newRoles[role][scope].delete(id);
      if (_newRoles[role][scope].size == 0) {
        delete _newRoles[role][scope];
      }
      if (Object.keys(_newRoles[role]).length == 0) {
        delete _newRoles[role];
      }
      setRoleManagement(_newRoles);
    }
  }

  const handleAppRoleChange = (e: any, value: string | null) => {
    if (!value || !appToModify) return;
    setModifyAppRole(value);

    setNewAdminApps({ ...newAdminApps, [appToModify]: value });
  };

  console.log(`adminCredentials.isSuperAdmin: ${adminCredentials.isSuperAdmin ? 'true' : 'false'}`)

  const disableAddRoleButton = () => {
    let _groups: Array<string> = [];
    let _globalRoles: Array<string> = [];
    Object.keys(roleManagement).map((role: string) => {
      Object.keys(roleManagement[role]).map((scope: string) => {
        if (scope == "Global" && !_globalRoles.includes(role)) {
          _globalRoles.push(role);
        } else {
          _groups = [..._groups, ...Array.from(roleManagement[role][scope])];
        }
      })
    })

    if (selectedRole != "" && selectedRoleScope != "") {
      if (selectedRoleScope == "Group" && !groupToModify) {
        return true;
      }
      if (selectedRoleScope == "Group" && groupToModify && _groups.includes(groupToModify)) {
        return true;
      }
      if (selectedRoleScope == "Global" && _globalRoles.includes(selectedRole)) {
        return true;
      }
      return false;
    }
    return true;
  }
  return (
    <>
      <Dialog
        onClose={props.cancel}
        open={props.modifyAdmin}
        fullScreen
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={props.cancel}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              {props.user === undefined
                ? 'Add new Admin'
                : `Modify ${props.user.id}`}
            </Typography>
            {savingAdmin ? (
              <CircularProgress />
            ) : (
              <Button
                color="inherit"
                disabled={
                  (id === '') || (!inEmailFormat(id))  /* ||
                (props.user === undefined &&
                  JSON.stringify(newAdminApps) === "{}" &&
                  !superAdmin) ||
                globalNoEdit
               */
                }
                onClick={restSaveAdmin}
              >
                Submit
              </Button>
            )}
          </Toolbar>
        </AppBar>
        <DialogContent className="form-section">
          <Grid container direction={'column'} spacing={4}>
            <Grid container item direction={'row'} spacing={4}>
              <Grid item md={6}>
                <TextField
                  name="email"
                  value={id}
                  onChange={(e) => setId(e.target.value)}
                  disabled={props.user !== undefined}
                  label="Email Address"
                  fullWidth
                />
              </Grid>
              {adminCredentials.isSuperAdmin ? (
                <Grid item md={6}>
                  <FormGroup>
                    <FormControlLabel
                      disabled={globalNoEdit || !adminCredentials.isSuperAdmin}
                      control={
                        <Checkbox
                          checked={superAdmin}
                          onChange={(event) =>
                            setSuperAdmin(event.target.checked)
                          }
                        />
                      }
                      label="Super Admin"
                    />
                  </FormGroup>
                </Grid>
              ) : (
                <></>
              )}
            </Grid>
            {!superAdmin && (
              <>
              <Grid container item spacing={4}>
                {props.organizations.length > 1 && (
                  <Grid item md={6}>
                    <Autocomplete
                        id="adminOrganization"
                        fullWidth
                        disableClearable
                        disabled={superAdmin || globalNoEdit}
                        value={assignedOrganization}
                        options={props.organizations.map(
                          (customer) => customer.id
                        )}
                        getOptionLabel={(option) =>
                          props.organizations.find(
                            (customer) => customer.id === option
                          )?.name ?? ''
                        }
                        renderInput={(params) => (
                          <TextField {...params} label={`Organization`} />
                        )}
                        onChange={(_, value) => {
                          setAssignedOrganization(value);
                          setAdditionalOrganizations(additionalOrganizations.filter(elm => { return elm !== value }));
                          if (nextAddOrganization === value) {
                            setNextAddOrganization('');
                          }
                        }}
                    />
                  </Grid>
                )}
                {(adminCredentials.isSuperAdmin ||
                  adminCredentials.isOrganizationAdmin) &&
                !superAdmin ? (
                  <Grid item md={6}>
                    <FormGroup>
                      <FormControlLabel
                        disabled={
                          globalNoEdit ||
                          (!adminCredentials.isSuperAdmin &&
                            !adminCredentials.isOrganizationAdmin)
                        }
                        control={
                          <Checkbox
                            checked={organizationAdmin}
                            onChange={(event) =>
                              setOrganizationAdmin(event.target.checked)
                            }
                          />
                        }
                        label="Organization Admin"
                      />
                    </FormGroup>
                  </Grid>
                ) : (
                  <></>
                )}
                </Grid>
                {((adminCredentials.isSuperAdmin || (adminCredentials.isOrganizationAdmin && adminCredentials.multiOrg)) && !superAdmin && organizationAdmin) && (
                  <>
                  <Grid item md={1}>
                        Additional Administered Organizations
                      </Grid>
                  {additionalOrganizations.map((additionalOrg, idx) => {
                    console.log("mapping additional org");
                    return (
                      <Grid container item spacing={4} id={`addtlOrg-${idx}`}>
                        <Grid item md={6}>
                          {props.organizations.find(
                            (customer) => customer.id === additionalOrg
                          )?.name ?? ''}
                        </Grid>
                        <Grid item md={6}>
                          <IconButton
                            edge="start"
                            color="inherit"
                            onClick={() => setAdditionalOrganizations(additionalOrganizations.filter(aorg => { return aorg !== additionalOrg }))}
                            aria-label="close"
                          >
                            <RemoveCircleOutlineOutlined />
                          </IconButton>
                        </Grid>
                      </Grid>
                    );
                  })}
                  </>)
                }
                {((adminCredentials.isSuperAdmin || (adminCredentials.isOrganizationAdmin && adminCredentials.multiOrg)) && !superAdmin && organizationAdmin) && (
                    <Grid container item spacing={4} id={`addNewOrgContainer`}>
                      <Grid item md={6}>
                        <Autocomplete
                          id="addNewOrganization"
                          fullWidth
                          disableClearable
                          disabled={superAdmin || globalNoEdit}
                          value={nextAddOrganization}
                          options={props.organizations.map(
                            (customer) => customer.id
                          ).filter(oid => (oid != assignedOrganization) && (!additionalOrganizations.includes(oid)))}
                          getOptionLabel={(option) =>
                            props.organizations.find(
                              (customer) => customer.id === option
                            )?.name ?? ''
                          }
                          renderInput={(params) => (
                            <TextField {...params} label={`Organization`} />
                          )}
                          onChange={(_, value) => {
                            setNextAddOrganization(value);
                          }}
                      />
                      </Grid>
                      <Grid item md={6}>
                      <Button disabled={!nextAddOrganization} onClick={() => {
                          if (nextAddOrganization) {
                            setAdditionalOrganizations([...additionalOrganizations, nextAddOrganization]);
                            setNextAddOrganization('');
                          }
                        }}>
                          Add Organization
                        </Button>
                      </Grid>
                    </Grid>
                  )
                }
              </>
            )}
            {(APPSETTINGS.enableAdminAppRoles && !superAdmin) ? (
              <Grid container item direction={'row'} spacing={4}>
                {applications.length > 1 ? (
                  <Grid item md={6}>
                    <Autocomplete
                      id="adminApplications"
                      className="adminSelect"
                      multiple
                      filterSelectedOptions
                      disabled={superAdmin || globalNoEdit}
                      value={selectedApps}
                      options={adminCredentials.adminApplications.filter(aApp => rolePermission(
                        'manageApplicationAdmins',
                        aApp.role,
                        adminCredentials.roleSpecificPermissions,
                        adminCredentials.isSuperAdmin
                      )).map(aApp => aApp.id)}
                      getOptionLabel={(option) =>
                        adminCredentials.allApplications[option]?.name ?? ''
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`Application ${superAdmin ? '' : '(Required)'
                          }`}
                        />
                      )}
                      onChange={handleChange}
                    />
                  </Grid>
                ) : (
                  <></>
                )}
                <Grid
                  item
                  md={6}
                  className={
                    JSON.stringify(appToModify) !== '{}' ? 'col-md-6' : 'hide'
                  }
                >
                  <Stack spacing={2}>
                    {applications.length !== 1 && selectedApps.length > 0 ? (
                      <>
                        <Autocomplete
                          className="adminSelect"
                          disableClearable
                          options={[''].concat(selectedApps)}
                          value={appToModify}
                          filterOptions={(option) =>
                            option.filter((opt) => opt !== '')
                          }
                          getOptionLabel={(option) =>
                            adminCredentials.allApplications[option]?.name ?? ''
                          }
                          renderInput={(params) => (
                            <TextField {...params} label="Application Role" />
                          )}
                          onChange={setAppRoleToModify}
                        />
                      </>
                    ) : (
                      <label htmlFor="applications">
                        {' '}
                        Application Permissions for{' '}
                        {adminCredentials.allApplications[appToModify ?? '']?.name ??
                          ''}{' '}
                      </label>
                    )}
                    {appToModify !== '' ? (
                      <Autocomplete
                        className="roleSelect"
                        disableClearable
                        disabled={superAdmin || globalNoEdit}
                        options={roleList}
                        value={modifyAppRole}
                        renderInput={(params) => (
                          <TextField {...params} label="Selected Role" />
                        )}
                        onChange={handleAppRoleChange}
                      />
                    ) : (
                      <></>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            ) : (
              <></>
            )}
            <Grid container item direction={'row'}>
              <Grid item md={12}>
                <Stack spacing={2}>
                  <label>Roles set for the user</label>
                  {roleManagement && Object.keys(roleManagement).map((role) =>
                    Object.keys(roleManagement[role]).map((scope: string) =>
                      Array.from(roleManagement[role][scope]).map((id: string) =>
                        (scope == "Application" || (scope == "Group" && !adminCredentials.allGroups.hasOwnProperty(id))) ? <></> : <Grid container key={scope + "-" + role + "-" + id} item direction={'row'}>
                          <Grid item md={3}>{role}</Grid>
                          <Grid item md={3}>{scope}</Grid>
                          <Grid item md={4}>
                            {scope == "Global" ? "" : adminCredentials.allGroups[id].displayName}
                          </Grid>
                          <Grid item md={2}>
                            <IconButton
                              edge="start"
                              color="inherit"
                              onClick={() => { deleteRoleFromUser(role, scope, id) }}
                              aria-label="delete"
                              disabled={!adminCanRemoveRole(adminCredentials.roleSpecificPermissions, adminCredentials.roles, { scope: { type: scope as ScopeType, objectId: id }, roleId: role }, adminCredentials.isSuperAdmin)}
                            >
                              <Delete />
                            </IconButton>
                          </Grid>
                        </Grid>
                      )
                    )
                  )}
                </Stack>
              </Grid>
            </Grid>
            {/* The below section will show only the roles as per new architecture */}
            <Grid container item direction={'row'} spacing={4}>
              <Grid item md={3}>
                <Autocomplete
                  className="roleSelect"
                  disableClearable
                  disabled={superAdmin || globalNoEdit}
                  options={assignableAdminRoles(
                    adminCredentials.roleSpecificPermissions,
                    adminCredentials.roles,
                    adminCredentials.isSuperAdmin
                  )}
                  value={selectedRole}
                  renderInput={(params) => (
                    <TextField {...params} label="Select a Role" />
                  )}
                  onChange={handleRoleChange}
                />
              </Grid>
              {selectedRole != "" && <Grid item md={3}>
                <Autocomplete
                  className="roleScopeSelect"
                  disableClearable
                  disabled={superAdmin || globalNoEdit}
                  options={assignableRoleScopeTypes(
                    adminCredentials.roleSpecificPermissions[selectedRole]['validScopeTypes'],
                    adminCredentials.roleSpecificPermissions,
                    adminCredentials.roles,
                    selectedRole,
                    adminCredentials.isSuperAdmin
                  )}
                  value={selectedRoleScope}
                  renderInput={(params) => (
                    <TextField {...params} label="Select a Scope" />
                  )}
                  onChange={handleRoleScopeChange}
                />
              </Grid>}
              {selectedRole != "" && selectedRoleScope == "Group" && adminCredentials.roleSpecificPermissions[selectedRole].isAssignable && <>
                <Grid item md={4}>
                  <Autocomplete
                    className="roleSelect"
                    disableClearable
                    disabled={superAdmin || globalNoEdit}
                    options={assignableRoleScopeIds(
                      "Group",
                      Object.keys(adminCredentials.allGroups),
                      adminCredentials.roleSpecificPermissions,
                      adminCredentials.roles,
                      selectedRole,
                      adminCredentials.isSuperAdmin
                    ).filter(
                      grpId => (!(["writeMembers", "writeOwners"].some(groupWritePerm => Object.keys(adminCredentials.roleSpecificPermissions[selectedRole]?.permissions ?? {}).includes(groupWritePerm) && adminCredentials.roleSpecificPermissions[selectedRole].permissions[groupWritePerm]))) || adminCredentials.allGroups[grpId].adminWritable
                    )}
                    getOptionLabel={(option: string) =>
                      adminCredentials.allGroups[option]?.displayName || ""
                    }
                    value={groupToModify}
                    renderInput={(params) => (
                      <TextField {...params} label="Select a Group" />
                    )}
                    onChange={handleGroupChange}
                  />

                </Grid> </>
              }

              <Grid item md={2}>
                <Button disabled={disableAddRoleButton()} variant="contained" onClick={() => {
                  let _role = Object.assign({}, roleManagement);
                  if (!_role.hasOwnProperty(selectedRole)) {
                    _role[selectedRole] = { [selectedRoleScope]: new Set() };
                    _role[selectedRole][selectedRoleScope].add(groupToModify);
                  } else {
                    if (_role[selectedRole].hasOwnProperty(selectedRoleScope)) {
                      _role[selectedRole][selectedRoleScope].add(groupToModify);
                    } else
                      _role[selectedRole] = { ..._role[selectedRole], [selectedRoleScope]: new Set() }
                    _role[selectedRole][selectedRoleScope].add(groupToModify);
                  }
                  setRoleManagement(_role);
                }}>
                  Add Role
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddAdmin;
